import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/utils/supabase";

interface CallbackData {
  id: string;
  order_no: string;
  to_user_name: string;
  from_user_name: string;
  create_time: string;
  msg_type: string;
  event: string;
  external_user_id: string;
  follow_user_id: string;
  chat_id: string;
  state: string;
  raw_data: string;
  created_at: string;
}

export default function WechatCallbackPage() {
  const [searchParams] = useSearchParams();
  const orderNo = searchParams.get("order_no") || "";
  
  const [callbacks, setCallbacks] = useState<CallbackData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);

  // 获取回调数据
  const fetchCallbacks = async () => {
    try {
      let query = supabase
        .from("wechat_callbacks")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (orderNo) {
        query = query.eq("order_no", orderNo);
      }
      
      const { data, error: queryError } = await query.limit(50);
      
      if (queryError) {
        setError("查询失败：" + queryError.message);
        return;
      }
      
      setCallbacks(data || []);
      setError("");
    } catch (err) {
      setError("查询异常：" + String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCallbacks();
    
    // 自动刷新
    let interval: ReturnType<typeof setInterval>;
    if (autoRefresh) {
      interval = setInterval(fetchCallbacks, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [orderNo, autoRefresh]);

  const formatDate = (str: string) => {
    if (!str) return "—";
    const d = new Date(str);
    return d.toLocaleString("zh-CN", { 
      year: "numeric", 
      month: "2-digit", 
      day: "2-digit", 
      hour: "2-digit", 
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const getEventLabel = (event: string) => {
    const map: Record<string, string> = {
      "add_external_contact": "添加客户",
      "del_external_contact": "删除客户",
      "change_external_contact": "客户信息变更",
      "add_half_external_contact": "半添加客户",
      "del_follow_user": "删除跟进人",
      "msg": "消息",
    };
    return map[event] || event || "未知";
  };

  const getEventColor = (event: string) => {
    const map: Record<string, string> = {
      "add_external_contact": "#07c160",
      "del_external_contact": "#e74c3c",
      "change_external_contact": "#f39c12",
      "add_half_external_contact": "#3498db",
    };
    return map[event] || "#7a5c2e";
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(160deg, #1a0000 0%, #4a0e0e 30%, #6b1a1a 60%, #3d0000 100%)",
        fontFamily: "'Noto Serif SC', serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;600;700&display=swap');
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .anim-fadeup { animation: fadeUp 0.5s ease both; }
      `}</style>

      {/* 顶部导航 */}
      <div
        className="sticky top-0 z-30 px-4 py-4 flex items-center justify-between"
        style={{ background: "rgba(26,0,0,0.95)", borderBottom: "1px solid rgba(200,146,42,0.3)" }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 flex items-center justify-center rounded-full"
            style={{ background: "rgba(7,193,96,0.2)" }}
          >
            <i className="ri-wechat-line text-sm" style={{ color: "#07c160" }} />
          </div>
          <div>
            <h1 className="text-base font-bold" style={{ color: "#e8b84b" }}>
              企业微信回调监控
            </h1>
            {orderNo && (
              <p className="text-xs" style={{ color: "#7a5c2e" }}>
                订单：{orderNo}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs cursor-pointer whitespace-nowrap"
            style={{
              background: autoRefresh ? "rgba(7,193,96,0.15)" : "rgba(255,255,255,0.05)",
              border: `1px solid ${autoRefresh ? "rgba(7,193,96,0.4)" : "rgba(200,146,42,0.2)"}`,
              color: autoRefresh ? "#07c160" : "#d4a96a",
            }}
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className={autoRefresh ? "ri-refresh-line" : "ri-pause-line"} />
            </div>
            {autoRefresh ? "自动刷新" : "已暂停"}
          </button>
          <button
            onClick={fetchCallbacks}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs cursor-pointer whitespace-nowrap"
            style={{
              background: "rgba(200,146,42,0.15)",
              border: "1px solid rgba(200,146,42,0.4)",
              color: "#e8b84b",
            }}
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-refresh-line" />
            </div>
            刷新
          </button>
        </div>
      </div>

      <div className="px-4 py-4 max-w-3xl mx-auto">
        {/* 回调URL信息 */}
        <div
          className="rounded-xl p-4 mb-4 anim-fadeup"
          style={{
            background: "rgba(253,243,227,0.06)",
            border: "1px solid rgba(200,146,42,0.25)",
          }}
        >
          <h2 className="text-sm font-bold mb-2" style={{ color: "#e8b84b" }}>
            回调地址配置
          </h2>
          <div
            className="text-xs break-all p-3 rounded-lg mb-2"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(200,146,42,0.2)",
              color: "#d4a96a",
              fontFamily: "monospace",
            }}
          >
            {`${window.location.origin}/wechat-callback${orderNo ? `?order_no=${orderNo}` : ""}`}
          </div>
          <p className="text-xs" style={{ color: "#7a5c2e" }}>
            将此地址配置到企业微信获客链接的回调URL中
          </p>
        </div>

        {/* 统计 */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            {
              label: "总回调数",
              value: callbacks.length,
              icon: "ri-message-3-line",
              color: "#e8b84b",
            },
            {
              label: "添加客户",
              value: callbacks.filter((c) => c.event === "add_external_contact").length,
              icon: "ri-user-add-line",
              color: "#07c160",
            },
            {
              label: "最近回调",
              value: callbacks[0] ? formatDate(callbacks[0].created_at).split(" ")[1] : "—",
              icon: "ri-time-line",
              color: "#d4a96a",
            },
          ].map((stat, i) => (
            <div
              key={i}
              className="rounded-xl px-3 py-3 anim-fadeup"
              style={{
                background: "rgba(253,243,227,0.06)",
                border: "1px solid rgba(200,146,42,0.25)",
                animationDelay: `${i * 0.1}s`,
              }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-7 h-7 flex items-center justify-center rounded-lg"
                  style={{ background: "rgba(200,146,42,0.15)" }}
                >
                  <i className={`${stat.icon} text-xs`} style={{ color: stat.color }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: "#d4a96a" }}>
                    {stat.label}
                  </p>
                  <p className="text-sm font-bold" style={{ color: "#fff" }}>
                    {stat.value}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 回调列表 */}
        <div
          className="rounded-xl overflow-hidden anim-fadeup"
          style={{
            background: "rgba(253,243,227,0.06)",
            border: "1px solid rgba(200,146,42,0.25)",
            animationDelay: "0.2s",
          }}
        >
          <div
            className="px-4 py-3 flex items-center justify-between"
            style={{ borderBottom: "1px solid rgba(200,146,42,0.2)" }}
          >
            <h2 className="text-sm font-bold" style={{ color: "#e8b84b" }}>
              回调记录
            </h2>
            <span className="text-xs" style={{ color: "#7a5c2e" }}>
              共 {callbacks.length} 条
            </span>
          </div>

          {loading ? (
            <div className="py-10 text-center">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-3">
                <i
                  className="ri-loader-4-line text-xl"
                  style={{ color: "#e8b84b", animation: "spin 1s linear infinite" }}
                />
              </div>
              <p className="text-sm" style={{ color: "#d4a96a" }}>
                加载中...
              </p>
            </div>
          ) : error ? (
            <div className="py-10 text-center">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-3">
                <i className="ri-error-warning-line text-xl" style={{ color: "#e74c3c" }} />
              </div>
              <p className="text-sm" style={{ color: "#e74c3c" }}>
                {error}
              </p>
            </div>
          ) : callbacks.length === 0 ? (
            <div className="py-10 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <i className="ri-inbox-line text-3xl" style={{ color: "#c8922a" }} />
              </div>
              <p className="text-sm" style={{ color: "#d4a96a" }}>
                暂无回调数据
              </p>
              <p className="text-xs mt-1" style={{ color: "#7a5c2e" }}>
                等待企业微信发送回调...
              </p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: "rgba(200,146,42,0.1)" }}>
              {callbacks.map((callback, idx) => (
                <div
                  key={callback.id}
                  className="px-4 py-3 anim-fadeup"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{
                          background: `${getEventColor(callback.event)}20`,
                          color: getEventColor(callback.event),
                          border: `1px solid ${getEventColor(callback.event)}40`,
                        }}
                      >
                        {getEventLabel(callback.event)}
                      </span>
                      <span className="text-xs" style={{ color: "#7a5c2e" }}>
                        {formatDate(callback.created_at)}
                      </span>
                    </div>
                    {callback.order_no && (
                      <span
                        className="text-xs px-2 py-0.5 rounded"
                        style={{
                          background: "rgba(200,146,42,0.1)",
                          color: "#c8922a",
                        }}
                      >
                        订单：{callback.order_no}
                      </span>
                    )}
                  </div>

                  <div className="space-y-1">
                    {callback.external_user_id && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: "#7a5c2e", minWidth: 80 }}>
                          客户ID：
                        </span>
                        <span
                          className="text-xs break-all"
                          style={{ color: "#d4a96a", fontFamily: "monospace" }}
                        >
                          {callback.external_user_id}
                        </span>
                      </div>
                    )}
                    {callback.follow_user_id && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: "#7a5c2e", minWidth: 80 }}>
                          跟进人：
                        </span>
                        <span
                          className="text-xs break-all"
                          style={{ color: "#d4a96a", fontFamily: "monospace" }}
                        >
                          {callback.follow_user_id}
                        </span>
                      </div>
                    )}
                    {callback.chat_id && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: "#7a5c2e", minWidth: 80 }}>
                          会话ID：
                        </span>
                        <span
                          className="text-xs break-all"
                          style={{ color: "#d4a96a", fontFamily: "monospace" }}
                        >
                          {callback.chat_id}
                        </span>
                      </div>
                    )}
                    {callback.state && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: "#7a5c2e", minWidth: 80 }}>
                          状态：
                        </span>
                        <span className="text-xs" style={{ color: "#d4a96a" }}>
                          {callback.state}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* 原始数据（可展开） */}
                  <details className="mt-2">
                    <summary
                      className="text-xs cursor-pointer"
                      style={{ color: "#7a5c2e" }}
                    >
                      查看原始数据
                    </summary>
                    <pre
                      className="mt-2 p-2 rounded-lg text-xs overflow-x-auto"
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        color: "#9a7a5a",
                        fontFamily: "monospace",
                        maxHeight: 200,
                        overflow: "auto",
                      }}
                    >
                      {callback.raw_data}
                    </pre>
                  </details>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 底部提示 */}
        <p className="mt-4 text-xs text-center" style={{ color: "#5a3a10" }}>
          此页面用于调试和监控企业微信回调数据
        </p>
      </div>
    </div>
  );
}